// Chronological per-user activity table rendered on UserDetailPage.
// Merges sim and course events into a single sorted stream: every sim STAGE
// (received / clicked / 2nd-stage / reported) is its own line with its own
// timestamp rather than one row showing only the final status, and course
// completions carry an outcome (passed / failed / failed-to-retake / expired)
// plus the quiz score. Timestamps render in the viewer's local time zone with
// the time of day and the stream sorts on the raw stamps, so same-day events
// order correctly.
//
// Postgres mapping: union of `simulations_sent` (unpivoted per stage) and
// `course_sends` filtered by user_id. Could become a `user_activity_v` view.
// Integration hook: `useUserActivityQuery(email, { limit })` cache 30 min.
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import MainCard from '../../../components/cards/MainCard';
import { fmtLocal } from './SendHistoryPage';

const EVENT_META = {
    received:       { label: 'Received',                  color: '#A0A8B8', faded: true },
    clicked:        { label: 'Clicked',                   color: '#FFCA28' },
    clicked_2nd:    { label: '2nd-stage Click',           color: '#FF5252' },
    reported:       { label: 'Reported',                  color: '#66BB6A' },
    assigned:       { label: 'Course Assigned',           color: '#00BFA5' },
    course_passed:  { label: 'Course Completed',          color: '#66BB6A' },
    course_failed:  { label: 'Course Failed',             color: '#FF5252' },
    course_retake:  { label: 'Course Failed - to Retake', color: '#FF7043' },
    course_expired: { label: 'Course Expired',            color: '#A0A8B8' },
};

function courseScoreOf(e) {
    if (e.correctAnswers == null || !e.totalQuestions) return null;
    return Math.round((e.correctAnswers / e.totalQuestions) * 100);
}

function buildEvents(simEvents, courseEvents) {
    const out = [];
    for (const e of simEvents) {
        const base = { entity: e.simName, entityKind: 'sim', score: null };
        out.push({ ...base, kind: 'received', date: e.sentAt || e.dateSent });
        // Each engaged stage is its own timeline line with its own stamp.
        // clickedAt falls back to landedAt in the data layer, so skip the
        // click line when it would just duplicate the 2nd-stage stamp.
        if (e.clickedAt && e.clickedAt !== e.landedAt) out.push({ ...base, kind: 'clicked', date: e.clickedAt });
        if (e.landedAt) out.push({ ...base, kind: 'clicked_2nd', date: e.landedAt });
        if (e.reportedAt) out.push({ ...base, kind: 'reported', date: e.reportedAt });
    }
    for (const e of courseEvents) {
        const base = { entity: e.courseName, entityKind: 'course' };
        const score = courseScoreOf(e);
        out.push({ ...base, kind: 'assigned', date: e.sentAt || e.dateSent, score: null });
        if (e.dateCompleted) {
            const kind = score == null ? 'course_expired' : score >= 80 ? 'course_passed' : 'course_failed';
            out.push({ ...base, kind, date: e.completedAt || e.dateCompleted, score });
        } else if (score != null) {
            // Scored but not completed = failed attempt awaiting a retake. The
            // events feed carries no updated-at stamp, so this line sits at the
            // assignment date until the backend exposes one.
            out.push({ ...base, kind: 'course_retake', date: e.sentAt || e.dateSent, score });
        }
    }
    return out.sort((a, b) => (Date.parse(b.date || 0) || 0) - (Date.parse(a.date || 0) || 0));
}

function KindLabel({ kind }) {
    const meta = EVENT_META[kind];
    return (
        <Box component="span" sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
            color: meta.color, whiteSpace: 'nowrap',
        }}>
            <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: meta.color, flexShrink: 0, opacity: meta.faded ? 0.55 : 1 }} />
            {meta.label}
        </Box>
    );
}

const cellSx = { py: 1.25, px: 2, borderBottom: '1px solid', borderColor: 'divider', verticalAlign: 'top', textAlign: 'left' };
const headSx = {
    py: 1.25, px: 2, textAlign: 'left',
    position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper',
    borderBottom: '1px solid', borderColor: 'divider',
    fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.disabled',
};

export default function UserActivityTimeline({ simEvents, courseEvents, drill }) {
    const events = useMemo(() => buildEvents(simEvents, courseEvents), [simEvents, courseEvents]);

    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                        Activity Timeline
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                        Every simulation and course event, newest first
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: 10, color: 'text.disabled', whiteSpace: 'nowrap', ml: 1 }}>
                    {events.length} event{events.length !== 1 ? 's' : ''}
                </Typography>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, maxHeight: 480, overflowY: 'auto' }}>
                {events.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: 'center', color: 'text.disabled', fontSize: 12 }}>
                        No activity for this user.
                    </Box>
                ) : (
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <Box component="thead">
                            <Box component="tr">
                                <Box component="th" sx={{ ...headSx, width: '130px' }}>Date</Box>
                                <Box component="th" sx={{ ...headSx, width: '90px' }}>Type</Box>
                                <Box component="th" sx={headSx}>Status</Box>
                                <Box component="th" sx={headSx}>Name</Box>
                                <Box component="th" sx={{ ...headSx, width: '70px', textAlign: 'right' }}>Score</Box>
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {events.map((ev, i) => {
                                const meta = EVENT_META[ev.kind];
                                const clickable = ev.entityKind === 'sim';
                                return (
                                    <Box
                                        component="tr"
                                        key={`${ev.date}-${ev.kind}-${ev.entity}-${i}`}
                                        onClick={() => clickable && drill && drill('sim-detail', ev.entity, { type: 'sim', value: ev.entity })}
                                        sx={{
                                            cursor: clickable ? 'pointer' : 'default',
                                            opacity: meta.faded ? 0.8 : 1,
                                            transition: 'background-color .15s',
                                            '&:hover': clickable ? { bgcolor: 'action.hover' } : undefined,
                                            '&:hover .entity': clickable ? { color: 'secondary.main' } : undefined,
                                        }}
                                    >
                                        <Box component="td" sx={{ ...cellSx, whiteSpace: 'nowrap', fontSize: 11, color: 'text.disabled', fontFeatureSettings: '"tnum"' }}>
                                            {fmtLocal(ev.date)}
                                        </Box>
                                        <Box component="td" sx={{ ...cellSx, fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                            {ev.entityKind === 'sim' ? 'Simulation' : 'Course'}
                                        </Box>
                                        <Box component="td" sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                                            <KindLabel kind={ev.kind} />
                                        </Box>
                                        <Box component="td" sx={cellSx}>
                                            <Typography className="entity" sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary', transition: 'color .15s' }}>
                                                {ev.entity}
                                            </Typography>
                                        </Box>
                                        <Box component="td" sx={{ ...cellSx, whiteSpace: 'nowrap', textAlign: 'right', fontSize: 11, fontWeight: 700, color: ev.score == null ? 'text.disabled' : ev.score >= 80 ? '#66BB6A' : '#FF5252' }}>
                                            {ev.score == null ? '' : `${ev.score}%`}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </Box>
        </MainCard>
    );
}
